// Wallet detection utilities

interface EthereumProvider {
  isMetaMask?: boolean;
  isTrust?: boolean;
  isCoinbaseWallet?: boolean;
  isTokenPocket?: boolean;
  isBraveWallet?: boolean;
  isPhantom?: boolean;
  isRabby?: boolean;
  providers?: EthereumProvider[];
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (
    event: string,
    handler: (...args: unknown[]) => void
  ) => void;
}

// Extend the existing Window interface
declare global {
  interface Window {
    trustwallet?: EthereumProvider;
    coinbaseWalletExtension?: EthereumProvider;
    coinbaseSolana?: unknown;
  }
}

export enum WalletType {
  METAMASK = 'metamask',
  TRUST = 'trust',
  COINBASE = 'coinbase',
  TOKENPOCKET = 'tokenpocket',
  BRAVE = 'brave',
  PHANTOM = 'phantom',
  RABBY = 'rabby',
  UNKNOWN = 'unknown',
}

export function detectWalletType(): WalletType {
  const ethereum = (window as any).ethereum as EthereumProvider | undefined;

  if (!ethereum) {
    return WalletType.UNKNOWN;
  }

  // Check if multiple providers exist
  if (ethereum.providers && Array.isArray(ethereum.providers)) {
    const braveProvider = ethereum.providers.find(p => p.isBraveWallet);
    if (braveProvider) {
      return WalletType.BRAVE;
    }

    const rabbyProvider = ethereum.providers.find(p => p.isRabby);
    if (rabbyProvider) {
      return WalletType.RABBY;
    }

    const trustProvider = ethereum.providers.find(p => p.isTrust);
    if (trustProvider) {
      return WalletType.TRUST;
    }

    const metaMaskProvider = ethereum.providers.find(
      p => p.isMetaMask && !p.isCoinbaseWallet && !p.isBraveWallet
    );
    if (metaMaskProvider) {
      return WalletType.METAMASK;
    }

    const coinbaseProvider = ethereum.providers.find(p => p.isCoinbaseWallet);
    if (coinbaseProvider) {
      return WalletType.COINBASE;
    }
  }

  // Check single provider
  if (ethereum.isBraveWallet) {
    return WalletType.BRAVE;
  }

  if (ethereum.isRabby) {
    return WalletType.RABBY;
  }

  if (ethereum.isTrust || window.trustwallet) {
    return WalletType.TRUST;
  }

  if (ethereum.isCoinbaseWallet) {
    return WalletType.COINBASE;
  }

  if (
    ethereum.isMetaMask &&
    !ethereum.isCoinbaseWallet &&
    !ethereum.isBraveWallet
  ) {
    return WalletType.METAMASK;
  }

  if (ethereum.isTokenPocket) {
    return WalletType.TOKENPOCKET;
  }

  return WalletType.UNKNOWN;
}

export function getWalletProvider(type: WalletType): EthereumProvider | null {
  const ethereum = (window as any).ethereum as EthereumProvider | undefined;

  // Special case: Coinbase Wallet Extension
  if (type === WalletType.COINBASE && window.coinbaseWalletExtension) {
    return window.coinbaseWalletExtension;
  }

  if (!ethereum) {
    return null;
  }

  // If there are multiple providers, find the right one
  if (ethereum.providers && Array.isArray(ethereum.providers)) {
    switch (type) {
      case WalletType.BRAVE:
        return ethereum.providers.find(p => p.isBraveWallet) || null;
      case WalletType.PHANTOM:
        return ethereum.providers.find(p => p.isPhantom) || null;
      case WalletType.RABBY:
        return ethereum.providers.find(p => p.isRabby) || null;
      case WalletType.TRUST:
        return ethereum.providers.find(p => p.isTrust) || null;
      case WalletType.METAMASK:
        return (
          ethereum.providers.find(
            p => p.isMetaMask && !p.isCoinbaseWallet && !p.isBraveWallet
          ) || null
        );
      case WalletType.COINBASE:
        return ethereum.providers.find(p => p.isCoinbaseWallet) || null;
      case WalletType.TOKENPOCKET:
        return ethereum.providers.find(p => p.isTokenPocket) || null;
      default:
        return ethereum;
    }
  }

  // Single provider case
  switch (type) {
    case WalletType.BRAVE:
      return ethereum.isBraveWallet ? ethereum : null;
    case WalletType.PHANTOM:
      return ethereum.isPhantom ? ethereum : null;
    case WalletType.RABBY:
      return ethereum.isRabby ? ethereum : null;
    case WalletType.TRUST:
      return ethereum.isTrust || window.trustwallet ? ethereum : null;
    case WalletType.METAMASK:
      return ethereum.isMetaMask &&
        !ethereum.isCoinbaseWallet &&
        !ethereum.isBraveWallet
        ? ethereum
        : null;
    case WalletType.COINBASE:
      return ethereum.isCoinbaseWallet ? ethereum : null;
    case WalletType.TOKENPOCKET:
      return ethereum.isTokenPocket ? ethereum : null;
    default:
      return ethereum;
  }
}

export function isWalletInstalled(type: WalletType): boolean {
  const provider = getWalletProvider(type);
  return provider !== null;
}

export async function connectWallet(
  type: WalletType
): Promise<string[] | null> {
  try {
    // Reuse getWalletProvider which already handles multi-provider detection
    const provider = getWalletProvider(type);
    if (!provider) return null;

    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    return accounts as string[];
  } catch (error) {
    console.error('Error during wallet connection:', error);
    return null;
  }
}

// Helper to check if we're in a mobile wallet's browser
export function isMobileWalletBrowser(): {
  isWalletBrowser: boolean;
  type: WalletType;
} {
  const userAgent = navigator.userAgent.toLowerCase();

  // Trust Wallet browser detection
  if (userAgent.includes('trustwallet') || window.trustwallet) {
    return { isWalletBrowser: true, type: WalletType.TRUST };
  }

  // MetaMask mobile browser detection
  if (userAgent.includes('metamask')) {
    return { isWalletBrowser: true, type: WalletType.METAMASK };
  }

  // Coinbase Wallet browser detection
  if (userAgent.includes('coinbasewallet')) {
    return { isWalletBrowser: true, type: WalletType.COINBASE };
  }

  // TokenPocket browser detection
  if (userAgent.includes('tokenpocket')) {
    return { isWalletBrowser: true, type: WalletType.TOKENPOCKET };
  }

  return { isWalletBrowser: false, type: WalletType.UNKNOWN };
}
